/**
 * Centralized Social Media option lists — now backend-driven.
 *
 * Master data (accounts, content types, languages, statuses, …) is served by
 * `GET /api/social-media/options` and cached at module scope for the session.
 * Screens consume it via the `useSocialMediaOptions()` hook (mirrors the
 * `useBrands()` pattern in `utils/brands.js`).
 *
 * The static `*_FALLBACK` constants below are **only** used before the initial
 * API response arrives or if the network fails. They must stay reasonably in
 * sync with `SM_*` constants in `backend/social_media_routes.py` so first
 * paint doesn't render empty dropdowns.
 *
 * `PLATFORMS` and `COLLEGES` intentionally continue to live in their own
 * utility modules (`platformIcons.js`, `colleges.js`) — those bundle rendering
 * logic (icon lookups + college metadata) alongside their identifiers. They
 * are re-exported here so consumers only need one import.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PLATFORMS as PLATFORMS_STATIC } from './platformIcons';
import { COLLEGES as COLLEGES_STATIC } from './colleges';

export {
  PlatformIcon,
  getPlatformIcon,
  renderPlatformIcons,
  findPlatformLink,
} from './platformIcons';

// `COLLEGES` / `COLLEGES_FOR_SELECT` are served live from
// `GET /api/social-media/options` (backed by SM Options → Colleges admin
// CRUD) via `useSocialMediaOptions()` — see `normalize()` below. The static
// list in `./colleges` is only used as `COLLEGES_STATIC` fallback until the
// API responds. Do not import `./colleges` directly for dropdowns.

// ─── Fallbacks (used only until the API response arrives) ────────────────────
const ACCOUNTS_FALLBACK = [
  'MH Cockpit',
  'TAN',
  'MH Cognition',
  'MH Intellect',
  'MH AgroTech',
  'MHC Global',
];

const ACCOUNTS_WITH_COLLEGE_EXPANSION_FALLBACK = ['MH Cockpit', 'MH Cognition'];

const CONTENT_TYPES_FALLBACK = ['Video', 'Poster', 'Carousel', 'Blog', 'Poll', 'Story'];

const LANGUAGES_FALLBACK = [
  'English', 'Tamil', 'Kannada', 'Malayalam', 'Telugu', 'Hindi', 'Urdu', 'Others',
];

const STATUSES_FALLBACK = [
  'Yet to Shoot', 'In Shoot', 'Yet to Edit', 'In Editing', 'In Corrections',
  'Not Approved', 'Yet to Deliver', 'Out Delivered', 'Waiting for Approval',
  'On-Hold', 'Scheduled', 'Published',
];

const LEGACY_STATUSES_FALLBACK = ['Pending', 'Completed'];

// COLLEGES_FALLBACK excludes 'NA' (analytics surfaces treat it as a bucket,
// not a real college). COLLEGES_FOR_SELECT_FALLBACK adds it back for form
// selectors. Both are only used until `/api/social-media/options` responds.
const COLLEGES_FALLBACK = COLLEGES_STATIC;
const COLLEGES_FOR_SELECT_FALLBACK = [...COLLEGES_STATIC, 'NA'];

const FALLBACK_OPTIONS = {
  PLATFORMS: PLATFORMS_STATIC,
  // PLATFORM_ICONS map is {platformName: iconKey}. Empty in the fallback path
  // — `getPlatformIcon()` gracefully falls back to name-based icon lookup.
  PLATFORM_ICONS: {},
  ACCOUNTS: ACCOUNTS_FALLBACK,
  ACCOUNTS_WITH_COLLEGE_EXPANSION: ACCOUNTS_WITH_COLLEGE_EXPANSION_FALLBACK,
  COLLEGES: COLLEGES_FALLBACK,
  COLLEGES_FOR_SELECT: COLLEGES_FOR_SELECT_FALLBACK,
  CONTENT_TYPES: CONTENT_TYPES_FALLBACK,
  LANGUAGES: LANGUAGES_FALLBACK,
  STATUSES: STATUSES_FALLBACK,
  LEGACY_STATUSES: LEGACY_STATUSES_FALLBACK,
  ACCOUNT_COLORS: {},
  COLLEGE_COLORS: {},
  CONTENT_TYPE_COLORS: {},
};

// ─── Module-level cache shared across all hook instances ─────────────────────
let cachedOptions = null;
let inFlight = null;
const listeners = new Set();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalize = (apiPayload) => ({
  PLATFORMS: apiPayload.platforms?.length ? apiPayload.platforms : PLATFORMS_STATIC,
  PLATFORM_ICONS: apiPayload.platform_icons || {},
  ACCOUNTS: apiPayload.accounts?.length ? apiPayload.accounts : ACCOUNTS_FALLBACK,
  ACCOUNTS_WITH_COLLEGE_EXPANSION:
    apiPayload.accounts_with_college_expansion?.length
      ? apiPayload.accounts_with_college_expansion
      : ACCOUNTS_WITH_COLLEGE_EXPANSION_FALLBACK,
  // Single source of truth: SM Options → Colleges. `COLLEGES_FOR_SELECT` is
  // the full active list (form selectors: create/edit/filter/bulk/calendar).
  // `COLLEGES` drops the 'NA' bucket for analytics surfaces (Account &
  // College Tracker) where it would be noise.
  COLLEGES_FOR_SELECT: apiPayload.colleges?.length ? apiPayload.colleges : COLLEGES_FOR_SELECT_FALLBACK,
  COLLEGES: apiPayload.colleges?.length
    ? apiPayload.colleges.filter((c) => c !== 'NA')
    : COLLEGES_FALLBACK,
  CONTENT_TYPES: apiPayload.content_types?.length ? apiPayload.content_types : CONTENT_TYPES_FALLBACK,
  LANGUAGES: apiPayload.languages?.length ? apiPayload.languages : LANGUAGES_FALLBACK,
  STATUSES: apiPayload.statuses?.length ? apiPayload.statuses : STATUSES_FALLBACK,
  LEGACY_STATUSES: apiPayload.legacy_statuses?.length
    ? apiPayload.legacy_statuses
    : LEGACY_STATUSES_FALLBACK,
  // {name: hex} maps — single source of truth for Account/College/Content
  // Type badge colours everywhere in the app. Empty until admin configures
  // (or the backend backfill runs); consumers must fall back gracefully.
  ACCOUNT_COLORS: apiPayload.account_colors || {},
  COLLEGE_COLORS: apiPayload.college_colors || {},
  CONTENT_TYPE_COLORS: apiPayload.content_type_colors || {},
});

const fetchOptions = (token) => {
  if (!token) return Promise.resolve(FALLBACK_OPTIONS);
  if (!inFlight) {
    inFlight = axios
      .get(`${API}/social-media/options`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        cachedOptions = normalize(res.data || {});
        return cachedOptions;
      })
      .catch(() => {
        cachedOptions = FALLBACK_OPTIONS;
        return cachedOptions;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

/** Force-refresh the cached social-media options. Consumers re-render automatically. */
export const refreshSocialMediaOptions = async (token) => {
  cachedOptions = null;
  const opts = await fetchOptions(token);
  listeners.forEach((cb) => cb(opts));
  return opts;
};

/**
 * React hook — returns the current Social Media option lists.
 * Fetches `GET /api/social-media/options` once per session and caches at
 * module scope. Consumers share the same cache, so N mounts trigger 1 request.
 */
export const useSocialMediaOptions = () => {
  const { token } = useAuth();
  const [opts, setOpts] = useState(cachedOptions || FALLBACK_OPTIONS);

  useEffect(() => {
    listeners.add(setOpts);
    return () => {
      listeners.delete(setOpts);
    };
    // `listeners` is a module-level singleton Set, intentionally excluded.
  }, []);

  useEffect(() => {
    if (cachedOptions || !token) return;
    let mounted = true;
    fetchOptions(token).then((next) => {
      if (mounted) setOpts(next);
    });
    return () => {
      mounted = false;
    };
    // `cachedOptions`/`fetchOptions` are module-level singletons (shared
    // cache across every hook instance), intentionally excluded so this
    // effect only re-runs on `token`.
  }, [token]);

  return opts;
};

export default useSocialMediaOptions;
